import clsx from 'clsx'
import { FC, useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import {
  PersonalAccountType as PersonalAccountTypeEnum,
  Premise
} from '@/api/models/auth/lib/auth.types'
import { useSelectedProject } from '@/api/models/main/hooks/useSelectedProject'
import { useSelectedProjectPremises } from '@/api/models/main/hooks/useSelectedProjectPremises'
import { AppSelect } from '@/components/shared/select'

import { PersonalAccountType } from '../../model/form.types'
import { getPersonalAccountsByRoomId } from '../model/utils'

type Props = {
  className?: string
  disabled?: boolean
  ownerOnly?: boolean
}

const RequestRoomPickerFormBlock: FC<Props> = ({
  className,
  disabled = false,
  ownerOnly = false
}) => {
  const { control, setValue, watch } = useFormContext<{
    room: Premise
    personalAccount: PersonalAccountType
  }>()
  const { selectedProject } = useSelectedProject()

  const selectedRoom = watch('room')

  const { rooms: allRooms } = useSelectedProjectPremises()

  const ownerPremiseIds = useMemo(() => {
    if (!ownerOnly) {
      return null
    }

    return selectedProject?.personalAccounts
      .filter(
        personalAccount =>
          personalAccount.type === PersonalAccountTypeEnum.Owner
      )
      .flatMap(personalAccount =>
        personalAccount.premises.map(premise => premise.id)
      )
  }, [ownerOnly, selectedProject])

  const rooms = useMemo(
    () =>
      ownerPremiseIds
        ? allRooms.filter(room => ownerPremiseIds.includes(room.id))
        : allRooms,
    [allRooms, ownerPremiseIds]
  )

  const personalAccountsForRoom = useMemo(() => {
    const accounts = selectedRoom?.id
      ? getPersonalAccountsByRoomId(selectedRoom.id, selectedProject)
      : []

    return ownerOnly
      ? accounts?.filter(
          personalAccount =>
            personalAccount.type === PersonalAccountTypeEnum.Owner
        )
      : accounts
  }, [selectedRoom?.id, selectedProject, ownerOnly])

  useEffect(() => {
    if (personalAccountsForRoom && personalAccountsForRoom.length > 0) {
      setValue('personalAccount', personalAccountsForRoom[0])
    }
  }, [personalAccountsForRoom, setValue])

  return (
    <>
      <div className={clsx(className, 'relative h-56')}>
        <Controller
          name="room"
          control={control}
          render={({ field }) => (
            <AppSelect
              disabled={disabled}
              label="Помещение"
              options={rooms}
              selectedOption={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>
      {personalAccountsForRoom && personalAccountsForRoom.length > 1 && (
        <div
          className={clsx(
            className,
            'relative z-[10] col-span-2 mt-16 h-56 lg:col-span-1 sm:mt-12'
          )}
        >
          <Controller
            name="personalAccount"
            control={control}
            render={({ field }) => (
              <AppSelect
                label="Лицевой счёт"
                options={personalAccountsForRoom}
                selectedOption={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      )}
    </>
  )
}

export { RequestRoomPickerFormBlock }
